'use client'

import React from 'react'
import Link from 'next/link'
import clsx from 'clsx'
import { TabsTrigger } from './tab'

interface TabButtonProps {
  value: string
  href: string
  isSelected: boolean
  children: React.ReactNode
  className?: string
  activeColor?: string
  inactiveColor?: string
  indicatorColor?: string
}

/**
 * TabButton - Componente reutilizable para botones de navegación en pestañas
 *
 * @param value - Valor único del tab (usado por Radix UI Tabs)
 * @param href - URL de destino al hacer clic
 * @param isSelected - Si la pestaña está seleccionada actualmente
 * @param children - Contenido a mostrar dentro del botón
 * @param className - Clases adicionales para personalizar el botón
 * @param activeColor - Color del texto cuando está activo (valor por defecto: #0866F2)
 * @param inactiveColor - Color del texto cuando está inactivo (valor por defecto: #60646C)
 * @param indicatorColor - Color del indicador de selección (valor por defecto: #0866F2)
 */
export function TabButton({
  value,
  href,
  isSelected,
  children,
  className,
  activeColor = '#0866F2',
  inactiveColor = '#60646C',
  indicatorColor = '#0866F2',
}: TabButtonProps) {
  return (
    <TabsTrigger value={value} asChild>
      <Link href={href} className="block h-full w-full">
        <div
          className={clsx([
            'relative flex h-full cursor-pointer items-center justify-center px-[20px] text-[13px]',
            {
              [`text-[${activeColor}]`]: isSelected,
              [`text-[${inactiveColor}] hover:bg-gray-100 hover:text-gray-700`]:
                !isSelected,
            },
            className,
          ])}>
          {children}
          {isSelected ? (
            <div
              className="absolute -bottom-[0.5px] left-0 right-0 h-[2px]"
              style={{ backgroundColor: indicatorColor }}
            />
          ) : null}
        </div>
      </Link>
    </TabsTrigger>
  )
}

export default TabButton